import clsx from 'clsx';
import { useField } from 'formik';
import React, { useRef } from 'react';
import { Mention, MentionsInput } from 'react-mentions';

import FieldError from '@components/Form/FieldError';
import { getMentionableUsers } from '@utils/backend-api/mentionableUsers';

type MentionableInputProps = {
  autoFocus?: boolean;
  contentId: number;
  contentType: string;
  className?: string;
  name: string;
  onChange: (e: React.ChangeEvent<unknown>) => void;
  placeholder?: string;
  required: boolean;
  value: string;
};

export default function MentionableInput({
  autoFocus,
  contentId,
  contentType,
  className,
  name,
  onChange,
  placeholder,
  required,
  value,
}: MentionableInputProps) {
  const [field, meta] = useField(name);
  const mentionInputRef = useRef<HTMLInputElement | null>();
  const fetchUsers = (query, callback) => {
    getMentionableUsers({ query: query, commentableId: contentId, commentableType: contentType })
      .then((res) =>
        res.users.map((user) => ({
          name: user.name,
          display: `@${user.username}`,
          id: user.id,
          username: user.username,
          pictureUrl: user.picture_url,
          companyName: user.company_name,
          title: user.title_at_company,
        }))
      )
      .then(callback)
      .catch((e) => {
        console.error('Error loading users: ', e);
      });
  };

  const renderMentionSuggestion = (entry, search) => {
    // Check console to see the number of times it executes for each character
    const nameRegex = new RegExp(search.substring(1).split('').join('\\W*'), 'i'); // abc -> /a\W*b\W*c/i
    // Wrap <span> around the matching part of the name
    const nameWithHighlight = entry.name.replace(nameRegex, '$&');
    const entryTitle = [entry.title, entry.companyName].filter((c) => !!c).join(' - ');

    return (
      <div
        className={clsx('flex', {
          'items-start': !entry.pictureUrl,
        })}
      >
        {entry.pictureUrl ? (
          <img
            src={entry.pictureUrl}
            alt={entry.name}
            className="mr-16 h-56 w-56 rounded-full bg-grayscale-beige"
          />
        ) : (
          <div className="mr-16 flex h-56 w-56 items-center justify-center rounded-full bg-grayscale-gray text-white">
            {entry.name[0].toUpperCase()}
          </div>
        )}
        <div className="flex flex-col justify-center">
          <span className="block font-suisse-intl-mono text-disclosure-md text-primary-magenta">
            {nameWithHighlight}
          </span>
          <span className="block font-suisse-screen text-disclosure-sm text-grayscale-gray">
            {entryTitle}
          </span>
        </div>
      </div>
    );
  };

  const handleBodyChange = (e, handleChange) => {
    const fabricatedEvent = { ...e, target: { ...e.target, name: 'body' } };
    handleChange(fabricatedEvent);
  };

  return (
    <>
      <MentionsInput
        {...field}
        name={name}
        required={required}
        className={className}
        autoFocus={autoFocus}
        onChange={(e) => handleBodyChange(e, onChange)}
        inputRef={mentionInputRef}
        value={value}
        placeholder={placeholder}
      >
        <Mention
          trigger="@"
          data={fetchUsers}
          renderSuggestion={renderMentionSuggestion}
          markup="[__display__]"
          appendSpaceOnAdd={true}
        />
      </MentionsInput>
      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </>
  );
}
